class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.boundClose = this.close.bind(this);
    this.boundOverlayClick = this.handleOverlayClick.bind(this);
    this.boundHeaderClick = this.handleHeaderCartClick.bind(this);
    this.boundHeaderKeydown = this.handleHeaderCartKeydown.bind(this);
    this.boundResize = this.scheduleGeometry.bind(this);
    this.geometryFrame = null;
    this.contentFrame = null;

    this.addEventListener('keyup', (event) => {
      if (event.code === 'Escape') this.close();
    });

    this.addEventListener('click', (event) => {
      const addButton = event.target.closest('[data-cart-bundle-add]');
      if (addButton && this.contains(addButton)) {
        this.addBundleProduct(addButton);
      }
    });

    this.bindOverlay();
    this.setHeaderCartIconAccessibility();
    this.observeContent();

    window.addEventListener('resize', this.boundResize, { passive: true });
    window.visualViewport?.addEventListener('resize', this.boundResize, { passive: true });
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.boundResize);
    window.visualViewport?.removeEventListener('resize', this.boundResize);
    this.contentObserver?.disconnect();
    if (this.geometryFrame) cancelAnimationFrame(this.geometryFrame);
    if (this.contentFrame) cancelAnimationFrame(this.contentFrame);
  }

  observeContent() {
    this.contentObserver?.disconnect();
    this.contentObserver = new MutationObserver(() => {
      if (this.contentFrame) cancelAnimationFrame(this.contentFrame);
      this.contentFrame = requestAnimationFrame(() => {
        this.contentFrame = null;
        this.syncDrawerState();
        this.bindOverlay();
        this.setHeaderCartIconAccessibility();
        this.updateGeometry();
      });
    });

    this.contentObserver.observe(this, { childList: true, subtree: true });
  }

  bindOverlay() {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (!overlay) return;
    overlay.removeEventListener('click', this.boundOverlayClick);
    overlay.addEventListener('click', this.boundOverlayClick);
  }

  handleOverlayClick(event) {
    event.preventDefault();
    this.close();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.removeEventListener('click', this.boundHeaderClick);
    cartLink.removeEventListener('keydown', this.boundHeaderKeydown);
    cartLink.addEventListener('click', this.boundHeaderClick);
    cartLink.addEventListener('keydown', this.boundHeaderKeydown);
  }

  handleHeaderCartClick(event) {
    event.preventDefault();
    this.open(event.currentTarget);
  }

  handleHeaderCartKeydown(event) {
    if (!['SPACE', 'ENTER'].includes(event.code.toUpperCase())) return;
    event.preventDefault();
    this.open(event.currentTarget);
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    this.syncDrawerState();
    this.updateGeometry();

    const panel = this.querySelector('.drawer__inner');
    panel?.setAttribute('aria-hidden', 'false');

    document.body.classList.add('overflow-hidden', 'cart-drawer-open');

    requestAnimationFrame(() => {
      this.classList.add('animate');
      requestAnimationFrame(() => this.classList.add('active'));
    });

    let focusApplied = false;
    const focusAfterOpen = () => {
      if (focusApplied || !this.classList.contains('active')) return;
      focusApplied = true;
      const container = this.querySelector('.drawer__inner');
      const focusElement = this.querySelector('.drawer__close') || container;
      if (container && focusElement && typeof trapFocus === 'function') {
        trapFocus(container, focusElement);
      }
    };

    panel?.addEventListener('transitionend', focusAfterOpen, { once: true });
    window.setTimeout(focusAfterOpen, 480);
  }

  close() {
    const panel = this.querySelector('.drawer__inner');
    this.classList.remove('active');
    panel?.setAttribute('aria-hidden', 'true');

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.activeElement);
    }

    document.body.classList.remove('overflow-hidden', 'cart-drawer-open');
    window.setTimeout(() => this.classList.remove('animate'), 440);
  }

  syncDrawerState() {
    const panel = this.querySelector('.drawer__inner');
    if (!panel) return;

    const isEmpty = Boolean(panel.querySelector('.drawer__inner-empty.ce-cart-empty'));
    this.classList.toggle('is-empty', isEmpty);
    panel.classList.toggle('ce-cart-panel--empty', isEmpty);
    panel.classList.toggle('ce-cart-panel--filled', !isEmpty);
  }

  scheduleGeometry() {
    if (this.geometryFrame) cancelAnimationFrame(this.geometryFrame);
    this.geometryFrame = requestAnimationFrame(() => {
      this.geometryFrame = null;
      this.updateGeometry();
    });
  }

  getViewportHeight() {
    return Math.round(
      window.visualViewport?.height ||
      document.documentElement.clientHeight ||
      window.innerHeight
    );
  }

  isVisible(element) {
    if (!element) return false;
    const style = getComputedStyle(element);
    const rect = element.getBoundingClientRect();
    return (
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      Number.parseFloat(style.opacity || '1') > 0 &&
      rect.width > 0 &&
      rect.height > 0 &&
      rect.bottom > 0 &&
      rect.top < this.getViewportHeight()
    );
  }

  findHeader() {
    const selectors = [
      'header.cstm-mobile-header',
      '.cstm-mobile-header',
      'sticky-header',
      '#shopify-section-header',
      '.section-header',
      'header.header'
    ];

    return selectors
      .flatMap((selector) => Array.from(document.querySelectorAll(selector)))
      .filter((element, index, list) => list.indexOf(element) === index)
      .filter((element) => this.isVisible(element))
      .sort((a, b) => b.getBoundingClientRect().bottom - a.getBoundingClientRect().bottom)[0] || null;
  }

  updateGeometry() {
    const panel = this.querySelector('.drawer__inner');
    if (!panel) return;

    const mobile = matchMedia('(max-width: 749px)').matches;
    const header = this.findHeader();
    const viewportHeight = this.getViewportHeight();
    let top;

    if (mobile) {
      /* The mobile drawer visually replaces/extends the rounded header. */
      const headerRect = header?.getBoundingClientRect();
      top = headerRect && headerRect.top >= 0
        ? Math.round(headerRect.top)
        : Math.max(56, Math.round(viewportHeight * 0.085));
    } else {
      const bottom = header?.getBoundingClientRect().bottom || 128;
      top = Math.round(bottom + 18);
    }

    const bottomGap = mobile
      ? 18
      : Math.max(28, Math.min(92, Math.round(viewportHeight * 0.105)));

    this.style.setProperty('--ce-cart-drawer-top', `${top}px`);
    this.style.setProperty('--ce-cart-drawer-bottom', `${bottomGap}px`);
  }

  async addBundleProduct(button) {
    if (!button || button.disabled || button.getAttribute('aria-busy') === 'true') return;

    const variantId = button.dataset.variantId;
    if (!variantId) return;

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');
      formData.append(
        'sections',
        this.getSectionsToRender().map((section) => section.id).join(',')
      );
      formData.append('sections_url', window.location.pathname);

      const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      });

      const parsedState = await response.json();
      if (!response.ok || parsedState.status) {
        throw new Error(parsedState.description || parsedState.message || 'Unable to add product.');
      }

      this.renderContents(parsedState);
    } catch (error) {
      console.error(error);
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }

  renderContents(parsedState) {
    if (!parsedState) return;

    const sections = parsedState.sections || {};
    this.productId =
      parsedState.id ||
      parsedState.items?.[0]?.id ||
      parsedState.items?.[0]?.product_id ||
      null;

    this.getSectionsToRender().forEach((section) => {
      const sectionHtml = sections[section.id];
      if (!sectionHtml) return;

      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;

      const replacement = this.getSectionInnerHTML(sectionHtml, section.selector);
      if (replacement == null) return;
      sectionElement.innerHTML = replacement;
    });

    this.bindOverlay();
    this.setHeaderCartIconAccessibility();
    this.syncDrawerState();
    this.updateGeometry();

    requestAnimationFrame(() => this.open());
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return null;
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    return documentFragment.querySelector(selector)?.innerHTML ?? null;
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble' }
    ];
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' }
    ];
  }
}

if (!customElements.get('cart-drawer-items')) {
  customElements.define('cart-drawer-items', CartDrawerItems);
}

class CenteredCartSlider extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;

    this.track = this.querySelector('[data-ce-cart-track], [data-cart-bundle-track]');
    this.cards = Array.from(this.track?.children || []);
    this.dotsContainer = this.querySelector('[data-ce-cart-dots], [data-cart-bundle-dots]');

    if (!this.track || !this.cards.length) return;

    this.dataset.initialized = 'true';
    this.activeIndex = 0;
    this.buildDots();
    this.bindEvents();

    const requested = Number.parseInt(this.dataset.initialSlide || '0', 10);
    requestAnimationFrame(() => this.scrollToCard(Number.isNaN(requested) ? 0 : requested, 'auto'));
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onScroll);
    this.track?.removeEventListener('keydown', this.onKeydown);
    this.resizeObserver?.disconnect();
  }

  buildDots() {
    if (!this.dotsContainer || this.cards.length < 2) return;
    this.dotsContainer.innerHTML = '';

    this.cards.forEach((card, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = this.matches('cart-bundle-slider')
        ? 'ce-cart-bundle-slider__dot'
        : 'ce-cart-slider__dot';
      dot.setAttribute('aria-label', `Go to slide ${index + 1}`);
      dot.setAttribute('aria-current', index === 0 ? 'true' : 'false');
      dot.addEventListener('click', () => this.scrollToCard(index));
      this.dotsContainer.appendChild(dot);
    });

    this.dots = Array.from(this.dotsContainer.children);
  }

  bindEvents() {
    let frame = null;

    this.onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        this.updateActiveCard();
      });
    };

    this.onKeydown = (event) => {
      if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      const direction = event.key === 'ArrowRight' ? 1 : -1;
      this.scrollToCard(Math.min(Math.max(this.activeIndex + direction, 0), this.cards.length - 1));
    };

    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.track.addEventListener('keydown', this.onKeydown);

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this.updateActiveCard());
      this.resizeObserver.observe(this.track);
    }
  }

  updateActiveCard() {
    const center = this.track.scrollLeft + this.track.clientWidth / 2;
    let closest = 0;
    let distance = Infinity;

    this.cards.forEach((card, index) => {
      const cardCenter = card.offsetLeft + card.offsetWidth / 2;
      const candidate = Math.abs(cardCenter - center);
      if (candidate < distance) {
        distance = candidate;
        closest = index;
      }
    });

    this.activeIndex = closest;
    this.dots?.forEach((dot, index) => {
      dot.setAttribute('aria-current', index === closest ? 'true' : 'false');
    });
  }

  scrollToCard(index, behavior = 'smooth') {
    const safe = Math.min(Math.max(index, 0), this.cards.length - 1);
    const card = this.cards[safe];
    if (!card) return;

    const left = card.offsetLeft - (this.track.clientWidth - card.offsetWidth) / 2;
    this.track.scrollTo({ left, behavior });
    this.activeIndex = safe;
    this.dots?.forEach((dot, dotIndex) => {
      dot.setAttribute('aria-current', dotIndex === safe ? 'true' : 'false');
    });
  }
}

if (!customElements.get('cart-empty-inline-slider')) {
  customElements.define('cart-empty-inline-slider', class extends CenteredCartSlider {});
}

if (!customElements.get('cart-bundle-slider')) {
  customElements.define('cart-bundle-slider', class extends CenteredCartSlider {});
}
